(() => {
  if (window.__SectionRenderPdpInitialized) return;
  window.__SectionRenderPdpInitialized = true;

  function docFrom(html) {
    return new DOMParser().parseFromString(html, "text/html");
  }

  function runScripts(container) {
    const scripts = [...container.querySelectorAll("script")];
    const pending = [];

    scripts.forEach((old) => {
      const s = document.createElement("script");

      [...old.attributes].forEach((attr) =>
        s.setAttribute(attr.name, attr.value),
      );

      if (old.src) {
        const already = [...document.scripts].some((x) => x.src === old.src);
        if (already) {
          old.remove();
          return;
        }
        // Dynamically inserted scripts are async by default; keep document order
        // (e.g. glide.js before product.js) and track when each one settles.
        s.async = false;
        pending.push(
          new Promise((resolve) => {
            s.addEventListener("load", resolve);
            s.addEventListener("error", resolve);
          }),
        );
        s.src = old.src;
      } else {
        s.textContent = old.textContent;
      }

      old.parentNode.replaceChild(s, old);
    });

    return Promise.all(pending);
  }

  function waitForScripts(promise, timeoutMs) {
    return Promise.race([
      promise,
      new Promise((resolve) => setTimeout(resolve, timeoutMs)),
    ]);
  }

  // One request per product URL per page view, shared by hover prefetch and click.
  const htmlCache = new Map();

  function fetchProductHTML(handle) {
    const url = getProductUrl(handle);
    if (!htmlCache.has(url)) {
      const req = fetch(url, {
        headers: { "X-Requested-With": "XMLHttpRequest" },
      }).then((res) => {
        if (!res.ok) throw new Error(`product fetch failed: ${res.status}`);
        return res.text();
      });
      req.catch(() => htmlCache.delete(url));
      htmlCache.set(url, req);
    }
    return htmlCache.get(url);
  }

  function getCurrentHandle() {
    const m = window.location.pathname.match(/\/products\/([^\/]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  }

  // Collagen toggles (Beyond Collagen, Collagen Peptides, their stick packs and flavors)
  // replace only the main product section, not all of #MainContent.
  const isSectionSwapHandle = (h) => !!h && h.includes("collagen");

  function getProductUrl(handle) {
    const currentHandle = getCurrentHandle();

    // Cross-flavor swaps: load each product's own assigned template (beyond-brew-lp,
    // beyond-brew-lp-vanilla, pdp-cleanup, etc.) — not the source page's ?view= suffix.
    if (currentHandle && currentHandle !== handle) {
      return `/products/${handle}`;
    }

    const ctx = window.igData?.redirect?.getContext?.();
    const redirected = ctx?.template?.isRedirected;
    return redirected
      ? `/products/${handle}?view=${ctx.template.suffix}`
      : `/products/${handle}`;
  }

  function scrollToFlavorSelector() {
    const target =
      document.querySelector("[data-select-your-type]") ||
      document.querySelector("[data-select-your-type-block]") ||
      document.querySelector(".additional-flavors-dropdown-container__icons") ||
      document.getElementById("product-info-bottom");

    if (!target) return false;

    const y = target.getBoundingClientRect().top + window.scrollY - 100;
    window.scrollTo({ top: Math.max(0, y), left: 0, behavior: "auto" });
    return true;
  }

  function scrollToFlavorSelectorWithRetries() {
    // beyond-brew-lp / beyond-brew-lp-vanilla: hero + carousels need extra layout passes
    [0, 50, 150, 400, 800].forEach((ms) => {
      setTimeout(() => scrollToFlavorSelector(), ms);
    });
  }

  function destroySplideIn(container) {
    try {
      if (window.TW?.destroyTestimonialSplide) {
        window.TW.destroyTestimonialSplide(container);
        return;
      }
    } catch (e) {}

    try {
      container
        .querySelectorAll(".testimonial-carousel-slider")
        .forEach((el) => {
          if (el.__splide && typeof el.__splide.destroy === "function") {
            try {
              el.__splide.destroy(true);
            } catch (e) {}
            el.__splide = null;
          }
        });
    } catch (e) {}
  }

  function initSplideIn(container) {
    if (!window.Splide) return;

    try {
      if (window.TW?.initTestimonialSplide) {
        window.TW.initTestimonialSplide(container);
        return;
      }
    } catch (e) {}

    try {
      container
        .querySelectorAll(".testimonial-carousel-slider")
        .forEach((carousel) => {
          if (carousel.__splide) return;

          const parentSection = carousel.closest(
            ".testimonial-carousel-section",
          );
          if (!parentSection) return;

          const autoplay =
            parentSection.getAttribute("data-autoplay") === "true";
          const autoplaySpeed =
            parseInt(parentSection.getAttribute("data-autoplay-speed"), 10) ||
            5000;
          const desktopSlides =
            parseInt(parentSection.getAttribute("data-desktop-slides"), 10) ||
            1;

          const splide = new Splide(carousel, {
            type: "loop",
            perPage: desktopSlides,
            perMove: 1,
            autoplay,
            interval: autoplaySpeed,
            pagination: true,
            arrows: true,
            speed: 600,
            gap: 20,
            focus: "center",
            breakpoints: {
              767: { perPage: 1, gap: 0 },
            },
          });

          splide.mount();
          carousel.__splide = splide;
        });
    } catch (e) {}
  }

  function nextFrame() {
    return new Promise((r) =>
      requestAnimationFrame(() => requestAnimationFrame(r)),
    );
  }

  async function collapseTypeBeforeSwap(fromEl) {
    try {
      const api = window.SelectYourTypeCollapse;
      if (api?.collapse) await api.collapse({ fromEl });
    } catch (e) {}
  }

  async function expandTypeAfterSwap(fromEl) {
    try {
      const api = window.SelectYourTypeCollapse;
      if (api?.expand) await api.expand({ fromEl });
    } catch (e) {}
  }

  function pickClosest(root, fromEl, selector) {
    if (!root) return null;
    const list = [...root.querySelectorAll(selector)];
    if (list.length === 0) return null;
    if (!fromEl) return list[0];

    const from = fromEl.getBoundingClientRect();
    let best = list[0];
    let bestScore = Infinity;

    for (const el of list) {
      const r = el.getBoundingClientRect();
      const dy = Math.abs(r.top - from.top);
      const dx = Math.abs(r.left - from.left);
      const score = dy * 1000 + dx; // prioritize vertical proximity
      if (score < bestScore) {
        bestScore = score;
        best = el;
      }
    }

    return best;
  }

  const COLLAGEN_TYPE_VISIBILITY_RULES = {
    "beyond-collagen": ["collagen-peptides"],
    "collagen-peptides": ["beyond-collagen"],
  };

  function resolveFirstTypeFromDoc(doc, collagenHandle) {
    const incomingScope = doc.querySelector("#MainContent") || doc;
    const incomingCollagen = incomingScope.querySelector(
      "[data-select-your-collagen]",
    );
    const incomingTypeBlock =
      pickClosest(
        incomingScope,
        incomingCollagen,
        "[data-select-your-type-block]",
      ) ||
      pickClosest(incomingScope, incomingCollagen, "[data-select-your-type]");
    if (!incomingTypeBlock) return null;

    const hide = COLLAGEN_TYPE_VISIBILITY_RULES[collagenHandle] || [];
    const items = [...incomingTypeBlock.querySelectorAll("[data-type-handle]")];

    for (const item of items) {
      const typeHandle = item.getAttribute("data-type-handle");
      if (!typeHandle || hide.includes(typeHandle)) continue;

      const anchor = item.querySelector("a.select__list--icons-item-anchor");
      return {
        handle: typeHandle,
        href: anchor?.href || `/products/${typeHandle}`,
      };
    }

    return null;
  }

  async function swapCollagenToFirstType(collagenHandle, clickedEl) {
    const html = await fetchProductHTML(collagenHandle);
    const doc = docFrom(html);
    const firstType = resolveFirstTypeFromDoc(doc, collagenHandle);

    const targetHandle = firstType?.handle || collagenHandle;
    const targetHref =
      firstType?.href || clickedEl?.href || `/products/${collagenHandle}`;

    return swapProduct(targetHandle, targetHref, clickedEl);
  }

  const withTimeout = (p, ms) =>
    Promise.race([p, new Promise((r) => setTimeout(r, ms))]);

  // Stylesheet links inside the swapped markup get torn down and re-added by innerHTML,
  // leaving the section unstyled for a few frames, so Glide mounts the gallery at the
  // wrong width and it jumps when the CSS lands. Load each one into <head> once, before
  // the swap, and drop the in-markup copies.
  function hoistStylesheets(incoming) {
    const loaded = new Set(
      [...document.head.querySelectorAll('link[rel="stylesheet"]')].map(
        (l) => l.href,
      ),
    );
    const loads = [
      ...incoming.querySelectorAll('link[rel="stylesheet"]'),
    ].map((l) => {
      const href = new URL(l.getAttribute("href"), location.href).href;
      l.remove();
      if (loaded.has(href)) return null;
      loaded.add(href);
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = href;
      const done = new Promise((r) => (link.onload = link.onerror = r));
      document.head.appendChild(link);
      return withTimeout(done, 2000);
    });
    return Promise.all(loads);
  }

  // Decode the incoming main image before swapping so the gallery doesn't flash blank.
  function preloadFirstSlide(incoming) {
    const src = incoming
      .querySelector(".js--section-product__slider img")
      ?.getAttribute("src");
    if (!src) return null;
    const img = new Image();
    img.src = src;
    return withTimeout(img.decode().catch(() => {}), 1500);
  }

  async function swapProduct(handle, href, clickedEl) {
    const main = document.querySelector("#MainContent");
    if (!main) throw new Error("Could not find #MainContent.");

    // ✅ CHANGE: animate Type selector when clicking the "Select Your X" row (collagen),
    // not when clicking the Type options.
    const clickedInCollagen = !!clickedEl?.closest(
      "[data-select-your-collagen]",
    );
    if (clickedInCollagen) {
      await collapseTypeBeforeSwap(clickedEl);
    }

    const productSection = (root) =>
      root.querySelector("#MainContent #product")?.closest(".shopify-section");
    const sectionOnly =
      isSectionSwapHandle(handle) && isSectionSwapHandle(getCurrentHandle());
    const currentSection = sectionOnly && productSection(document);

    const html = await fetchProductHTML(handle);
    const doc = docFrom(html);

    const incomingMain = doc.querySelector("#MainContent");
    if (!incomingMain) throw new Error("Incoming #MainContent not found.");

    const incomingSection = currentSection && productSection(doc);
    const target = incomingSection ? currentSection : main;
    const incoming = incomingSection || incomingMain;

    await Promise.all([
      incomingSection && hoistStylesheets(incoming),
      preloadFirstSlide(incoming),
    ]);

    destroySplideIn(target);
    target.innerHTML = incoming.innerHTML;

    // Update URL first so inline scripts that read location see the new handle.
    history.replaceState({ handle }, "", href || `/products/${handle}`);
    if (incomingSection) document.title = doc.title;

    const scriptsLoaded = runScripts(target);
    reinitYotpo({ handle });

    if (!incomingSection) {
      await nextFrame();
      scrollToFlavorSelectorWithRetries();
    }
    // Don't fire the swap event (which runs product.js init) until external
    // scripts such as glide.js have loaded (bounded so a stalled request can't hang the swap).
    await waitForScripts(scriptsLoaded, 5000);

    initSplideIn(target);

    if (window.Shopify?.PaymentButton) window.Shopify.PaymentButton.init();
    document.dispatchEvent(
      new CustomEvent(incomingSection ? "pdp:main-swapped" : "pdp:swapped", {
        detail: { handle },
      }),
    );

    if (clickedInCollagen) {
      await expandTypeAfterSwap(clickedEl);
    }

    function reinitYotpo(detail) {
      try {
        if (window.yotpo) {
          if (typeof window.yotpo.initWidgets === "function")
            window.yotpo.initWidgets();
          if (typeof window.yotpo.refreshWidgets === "function")
            window.yotpo.refreshWidgets();
        }
        if (
          window.Yotpo?.widgets &&
          typeof window.Yotpo.widgets.refresh === "function"
        ) {
          window.Yotpo.widgets.refresh();
        }
        document.dispatchEvent(new CustomEvent("yotpo:refresh", { detail }));
      } catch (e) {
        console.warn("Yotpo reinit failed", e);
      }
    }
  }

  document.addEventListener(
    "click",
    (e) => {
      const a = e.target.closest(
        "a.select__list--icons-item-anchor, a.select-your-x-item-anchor",
      );
      if (!a) return;

      if (a.classList.contains("is-active")) {
        e.preventDefault();
        e.stopPropagation();
        if (a.classList.contains("select__list--icons-item-anchor")) {
          scrollToFlavorSelectorWithRetries();
        }
        return;
      }

      if (
        e.metaKey ||
        e.ctrlKey ||
        e.shiftKey ||
        e.altKey ||
        a.target === "_blank"
      )
        return;

      const handle = a.dataset.handle;
      if (!handle) return;

      e.preventDefault();
      e.stopPropagation();

      const isCollagenSwitch = a.classList.contains(
        "select-your-x-item-anchor",
      );
      const action = isCollagenSwitch
        ? swapCollagenToFirstType(handle, a)
        : swapProduct(handle, a.href, a);

      Promise.resolve(action).catch((err) => {
        console.error(err);
        window.location.href = a.href;
      });
    },
    true,
  );

  // Collagen toggles: start the fetch on hover/touch/focus so the click swaps sooner.
  function prefetchToggle(e) {
    const a = e.target.closest?.(
      "a.select__list--icons-item-anchor, a.select-your-x-item-anchor",
    );
    const handle = a?.dataset.handle;
    if (!handle || a.classList.contains("is-active")) return;
    if (!isSectionSwapHandle(handle) || !isSectionSwapHandle(getCurrentHandle()))
      return;
    fetchProductHTML(handle).catch(() => {});
  }
  ["pointerover", "touchstart", "focusin"].forEach((type) =>
    document.addEventListener(type, prefetchToggle, { passive: true }),
  );

  // PDP rating strip: survives AJAX swaps (inline onclick / product.js only bind first paint).
  document.addEventListener(
    "click",
    (e) => {
      const strip = e.target.closest(
        "#MainContent #product .yotpo-reviews-container",
      );
      if (!strip) return;

      const reviewSection = document.getElementById("yotpo-reviews-main-widget");
      if (!reviewSection) return;

      reviewSection.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "nearest",
      });
    },
    true,
  );
})();
